import React from 'react';
import { BookOpen, BellRing, RefreshCw, BarChart3, Users, Star, Laptop, MessageSquare, Phone, Heart } from 'lucide-react';

const IntroStudySystem: React.FC = () => {
    return (
        <div className="animate-in fade-in duration-700 bg-[#0B132B]">
            {/* Hero Section */}
            <section className="py-16 md:py-20 px-4 md:px-8 bg-gradient-to-br from-[#0B132B] to-[#111A36]/30">
                <div className="max-w-5xl mx-auto text-center space-y-6">
                    <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-indigo-50/5 text-indigo-300 font-black text-[10px] uppercase tracking-wider border border-indigo-500/25">
                        Study System
                    </div>
                    <h1 className="text-2xl sm:text-4xl md:text-5xl lg:text-6xl font-black text-white leading-tight break-keep">
                        몰입의 가치를 만드는 <br />
                        <span className="text-indigo-400">6대 명품 학습 케어</span>
                    </h1>
                    <p className="text-sm sm:text-base md:text-lg lg:text-xl text-slate-400 font-medium max-w-2xl mx-auto break-keep">
                        단순한 주입식 교육을 넘어, 정성우학원만의 독보적인 시스템으로 <br />
                        학생 한 명 한 명의 완벽한 자기주도학습 성장을 이끌어냅니다.
                    </p>
                </div>
            </section>

            {/* Cares Grid Section */}
            <section className="pb-16 md:pb-24 px-4 md:px-8 max-w-6xl mx-auto">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
                    {[
                        {
                            no: "01",
                            title: "담임선생님 직접관리제",
                            desc: "수업만 하고 끝나는 강의가 아닙니다. 담당 선생님이 학생의 진도, 이해도, 성향까지 파악하여 개인별 맞춤 피드백을 밀착 지원합니다.",
                            img: "/system_teacher.jpg"
                        },
                        {
                            no: "02",
                            title: "복습 클리닉",
                            desc: "한 번 배운 내용을 완벽하게 자신의 것으로 소화할 수 있도록 당일 평가 및 피드백, 오답 노트 정리를 유도하는 체계적인 클리닉을 제공합니다.",
                            img: "/system_clinic.jpg"
                        },
                        {
                            no: "03",
                            title: "1:1맞춤 밀착관리",
                            desc: "학생 개개인의 취약 부분과 학습 속도를 감안하여 개인 과외 수준의 1:1 밀착 코칭 및 오답 분석을 통해 빈틈없는 성장을 만들어 냅니다.",
                            img: "/system_oneonone.jpg"
                        },
                        {
                            no: "04",
                            title: "학교별 맞춤 R&D",
                            desc: "학교별 출제 경향, 역대 기출문제 데이터베이스, 부교재 완벽 분석을 바탕으로 내신 고득점을 위한 전용 교재와 최적의 시험 직전 대비 전략을 수립합니다.",
                            img: "/system_rnd.jpg"
                        },
                        {
                            no: "05",
                            title: "기출동형모의고사 실시",
                            desc: "실전과 동일한 환경에서 정기적으로 평가를 실시하여 시간 분배 훈련과 취약 단원을 진단하고 성적 추이를 정밀 분석합니다.",
                            img: "/system_test_v2.jpg"
                        },
                        {
                            no: "06",
                            title: "철저한 오답관리",
                            desc: "틀린 문제의 단순 확인을 넘어 오답 노트 정리와 유사 문제 반복 풀이를 통해 취약점을 확실하게 보완합니다.",
                            img: "/system_wrong.jpg"
                        }
                    ].map((item, idx) => (
                        <div
                            key={idx}
                            className="bg-[#111A36]/30 border border-indigo-950/40 rounded-[2.5rem] overflow-hidden group hover:border-indigo-500/20 transition-all duration-300 shadow-lg flex flex-col"
                        >
                            <div className="aspect-[16/10] overflow-hidden relative">
                                <img
                                    src={item.img}
                                    alt={item.title}
                                    loading="lazy"
                                    decoding="async"
                                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                                />
                                <div className="absolute inset-0 bg-gradient-to-t from-slate-950/60 via-transparent to-transparent"></div>
                                <span className="absolute top-6 left-6 w-10 h-10 rounded-xl bg-indigo-600/90 text-white flex items-center justify-center font-black text-sm shadow-md">
                                    {item.no}
                                </span>
                            </div>
                            <div className="p-6 md:p-8 text-left space-y-3 flex-1 flex flex-col justify-center">
                                <h3 className="text-base sm:text-lg font-black text-white break-keep">{item.title}</h3>
                                <p className="text-slate-400 text-xs sm:text-sm font-medium leading-relaxed break-keep">{item.desc}</p>
                            </div>
                        </div>
                    ))}
                </div>
            </section>

            {/* JUNG SUNG WOO Management System Section */}
            <section className="py-16 md:py-24 px-4 md:px-8 bg-[#070D1E] text-white overflow-hidden relative border-t border-indigo-950/20">
                <div className="max-w-5xl mx-auto relative z-10 space-y-16">
                    <div className="text-center space-y-4">
                        <span className="text-[10px] md:text-xs font-black uppercase tracking-widest text-indigo-400">JUNG SUNG WOO SYSTEM</span>
                        <h2 className="text-xl sm:text-2xl md:text-3xl font-black text-white leading-tight tracking-tight break-keep">
                            빈틈을 허락하지 않는 <br />
                            <span className="text-transparent bg-clip-text bg-gradient-to-r from-indigo-400 to-sky-400">정성우 관리 시스템</span>
                        </h2>
                        <p className="text-slate-400 font-medium text-xs sm:text-sm md:text-base break-keep">
                            확실한 학습 성장을 만드는 정성우학원의 10가지 밀착 관리 원칙
                        </p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
                        {[
                            {
                                no: "01",
                                title: "철저한 나머지",
                                desc: "숙제 완성도까지 확인·80점 미만 시 나머지 지도",
                                icon: BookOpen,
                                iconColor: "text-rose-400 bg-rose-500/10"
                            },
                            {
                                no: "02",
                                title: "출결 엄수",
                                desc: "등원 즉시 알림 문자·지각 시 즉각 연락",
                                icon: BellRing,
                                iconColor: "text-amber-400 bg-amber-500/10"
                            },
                            {
                                no: "03",
                                title: "100% 보충",
                                desc: "결석 시 전원 보충 수업·학습 결손 Zero",
                                icon: RefreshCw,
                                iconColor: "text-teal-400 bg-teal-500/10"
                            },
                            {
                                no: "04",
                                title: "학기별 역량 평가",
                                desc: "6개월마다 성취도·태도 종합 평가 후 반 배정",
                                icon: BarChart3,
                                iconColor: "text-indigo-400 bg-indigo-500/10"
                            },
                            {
                                no: "05",
                                title: "1:1 전담 케어",
                                desc: "담임선생님의 1:1 전방위 밀착 케어",
                                icon: Users,
                                iconColor: "text-purple-400 bg-purple-500/10"
                            },
                            {
                                no: "06",
                                title: "명예의 전당",
                                desc: "성적, 대입, 고입, 향상 우수자 시상으로 동기 유발",
                                icon: Star,
                                iconColor: "text-rose-400 bg-rose-500/10"
                            },
                            {
                                no: "07",
                                title: "최적의 자습실 환경",
                                desc: "집중력을 극대화하는 개인 지정 자습석 및 쾌적한 학습 공간 제공",
                                icon: Laptop,
                                iconColor: "text-blue-400 bg-blue-500/10"
                            },
                            {
                                no: "08",
                                title: "정성우톡 상시 소통",
                                desc: "설명회 영상·공지 실시간 카톡 공유",
                                icon: MessageSquare,
                                iconColor: "text-indigo-400 bg-indigo-500/10"
                            },
                            {
                                no: "09",
                                title: "정규 상담",
                                desc: "담임 직접 전화·강점·방향 심층 안내",
                                icon: Phone,
                                iconColor: "text-amber-400 bg-amber-500/10"
                            },
                            {
                                no: "10",
                                title: "맞춤 학생상담",
                                desc: "숙제장·목표 설정·자기주도 학습 훈련",
                                icon: Heart,
                                iconColor: "text-rose-400 bg-rose-500/10"
                            }
                        ].map((item, idx) => (
                            <div
                                key={idx}
                                className="flex items-center justify-between p-5 md:p-6 rounded-2xl bg-[#111A36]/40 border border-indigo-950/60 hover:border-indigo-500/20 transition-all group relative overflow-hidden"
                            >
                                <div className="flex items-center gap-4 relative z-10">
                                    <div className={`shrink-0 w-11 h-11 rounded-xl flex items-center justify-center ${item.iconColor}`}>
                                        <item.icon size={20} />
                                    </div>
                                    <div className="text-left space-y-1">
                                        <h4 className="font-black text-slate-200 text-sm md:text-base break-keep">{item.title}</h4>
                                        <p className="text-[11px] md:text-xs text-slate-400 font-medium leading-relaxed break-keep">{item.desc}</p>
                                    </div>
                                </div>
                                <span className="text-3xl md:text-5xl font-black text-indigo-500/5 select-none relative z-10 shrink-0">
                                    {item.no}
                                </span>
                            </div>
                        ))}
                    </div>
                </div>
                <div className="absolute top-0 right-0 w-96 h-96 bg-indigo-600/10 blur-[100px] rounded-full"></div>
            </section>
        </div>
    );
};

export default IntroStudySystem;
